import type { FC, ReactNode } from 'react'
import type { Speaker } from '@/types'

import Image from 'next/image'
import Link from 'next/link'

import { cn } from '@/lib/utils'

type SpeakerCardProps = {
    speaker: Speaker
    className?: string
    priority?: boolean
}

export const SpeakerCard: FC<SpeakerCardProps> = ({ speaker, className, priority = false }): ReactNode => {
    return (
        <Link
            href={`/speakers/${speaker.slug}`}
            className={cn(
                'group flex h-full flex-col overflow-hidden rounded-[9px] border border-line bg-[#0d0d0d]',
                'transition-colors duration-500 hover:border-[#454545]',
                className
            )}
        >
            <div className='relative aspect-[4/5] w-full overflow-hidden'>
                <Image
                    src={speaker.image}
                    alt={speaker.name}
                    fill
                    priority={priority}
                    sizes='(max-width: 810px) 50vw, (max-width: 1360px) 33vw, 320px'
                    className='object-cover saturate-[0.9] transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]'
                />

                <span aria-hidden='true' className='fw-noise absolute inset-0 opacity-[0.08]' />
            </div>

            <div className='flex flex-col gap-1 border-t border-line px-4 py-4'>
                <span className='font-sans text-[16px] font-medium leading-[1.3em] text-ink'>
                    {speaker.name}
                </span>

                <span className='font-body text-[14px] leading-[1.4em] text-ink-mute'>
                    {speaker.role} · {speaker.company}
                </span>
            </div>
        </Link>
    )
}
